"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.searchResultTabResultCount = void 0;
const tslib_1 = require("tslib");
const lite_1 = require("algoliasearch/lite");
const app_1 = require("app");
const SearchResultTabService_1 = require("./SearchResultTabService");
const constants_1 = require("../constants");
const typings_1 = require("../typings");
class SearchResultTabResultCount {
    constructor() {
        this.searchClientCache = new Map();
        this.getMap = (tabs_1, ...args_1) => tslib_1.__awaiter(this, [tabs_1, ...args_1], void 0, function* (tabs, documentPreselectedLanguages = [], culture = constants_1.CHINA_LOCALE, analyticsTags = []) {
            if (!tabs || tabs.length === 0) {
                return {};
            }
            const urlSearchQuery = app_1.UrlService.GetQueryValueFromUrl('query') || '*';
            const duplicatedKeys = new Set();
            const groupedTabs = new Map();
            const resultCountMap = {};
            tabs.forEach(tab => {
                const groupKey = this.getClientCacheKey(tab.applicationsKey, tab.apiKey);
                const currentGroup = groupedTabs.get(groupKey);
                if (currentGroup) {
                    currentGroup.push(tab);
                    return;
                }
                groupedTabs.set(groupKey, [tab]);
            });
            const groupedResults = yield Promise.all(Array.from(groupedTabs.values()).map(group => this.getGroupedTabResults(group, urlSearchQuery, documentPreselectedLanguages, culture, analyticsTags)));
            groupedResults.forEach(groupResult => {
                groupResult.forEach(({ key, resultCount }) => {
                    if (Object.prototype.hasOwnProperty.call(resultCountMap, key)) {
                        duplicatedKeys.add(key);
                    }
                    resultCountMap[key] = resultCount;
                });
            });
            if (duplicatedKeys.size > 0) {
                console.warn('[SearchResultTabResultCount] Duplicate tab keys detected:', [
                    ...duplicatedKeys,
                ]);
            }
            return resultCountMap;
        });
        this.getGroupedTabResults = (tabs_1, query_1, documentPreselectedLanguages_1, ...args_1) => tslib_1.__awaiter(this, [tabs_1, query_1, documentPreselectedLanguages_1, ...args_1], void 0, function* (tabs, query, documentPreselectedLanguages, culture = constants_1.CHINA_LOCALE, analyticsTags = []) {
            if (tabs.length === 0) {
                return [];
            }
            const searchClient = this.getSearchClient(tabs[0].applicationsKey, tabs[0].apiKey);
            const searchQueries = tabs.map(tab => {
                const filters = this.getFiltersConfig(tab, documentPreselectedLanguages, culture);
                return {
                    indexName: tab.indexName,
                    params: {
                        hitsPerPage: 0,
                        query,
                        filters,
                        analyticsTags,
                    },
                };
            });
            try {
                const { results } = yield searchClient.search(searchQueries);
                return tabs.map((tab, index) => {
                    var _a;
                    return ({
                        key: tab.key,
                        resultCount: ((_a = results[index]) === null || _a === void 0 ? void 0 : _a['nbHits']) || 0,
                    });
                });
            }
            catch (error) {
                console.warn('[SearchResultTabResultCount] Failed to load grouped tab result counts', {
                    tabKeys: tabs.map(tab => tab.key),
                    indexNames: tabs.map(tab => tab.indexName),
                    error,
                });
                return tabs.map(tab => ({ key: tab.key, resultCount: 0 }));
            }
        });
        this.getFiltersConfig = (tab, documentPreselectedLanguages, culture) => {
            const filtersConfig = this.getInitialFiltersConfig(tab, documentPreselectedLanguages, culture);
            const isProductTab = tab.key === typings_1.SearchResultTabKeyEnum.Products;
            const defaultConfig = isProductTab
                ? `${constants_1.DEFAULT_FILTER_CONFIGURATION} ${constants_1.PRODUCT_FILTER_EXTRA_CONFIGURATION}`
                : constants_1.DEFAULT_FILTER_CONFIGURATION;
            return defaultConfig + (filtersConfig ? ` AND ${filtersConfig}` : '');
        };
        this.getInitialFiltersConfig = (tab, documentPreselectedLanguages = [], culture = constants_1.CHINA_LOCALE) => {
            if (!tab) {
                return '';
            }
            const { refinementList, menu, hierarchicalMenu } = SearchResultTabService_1.searchResultTabService.getFiltersWithUrlPriority(tab, documentPreselectedLanguages, culture);
            const filters = [];
            Object.entries(menu).forEach(([attribute, value]) => {
                this.appendFacetFilter(attribute, value, filters);
            });
            Object.entries(refinementList).forEach(([attribute, value]) => {
                this.appendFacetFilter(attribute, value, filters);
            });
            Object.entries(hierarchicalMenu).forEach(([attribute, value]) => {
                this.appendFacetFilter(attribute, value, filters);
            });
            return filters.join(' AND ');
        };
        this.getClientCacheKey = (applicationKey, apiKey) => `${applicationKey}::${apiKey}`;
        this.getSearchClient = (applicationKey, apiKey) => {
            const cacheKey = this.getClientCacheKey(applicationKey, apiKey);
            const cachedClient = this.searchClientCache.get(cacheKey);
            if (cachedClient) {
                return cachedClient;
            }
            const searchClient = (0, lite_1.liteClient)(applicationKey, apiKey);
            this.searchClientCache.set(cacheKey, searchClient);
            return searchClient;
        };
        this.escapeFilterValue = (value) => value.replace(/\\/g, String.raw `\\`).replace(/"/g, String.raw `\"`);
        this.normalizeFilterValue = (value) => {
            if (typeof value === 'string') {
                return value.trim();
            }
            if (typeof value === 'number' || typeof value === 'boolean') {
                return `${value}`.trim();
            }
            return '';
        };
        this.normalizeValues = (value) => {
            if (Array.isArray(value)) {
                return value.map(item => this.normalizeFilterValue(item)).filter(Boolean);
            }
            if (value === undefined || value === null) {
                return [];
            }
            const normalizedValue = this.normalizeFilterValue(value);
            return normalizedValue ? [normalizedValue] : [];
        };
        this.appendFacetFilter = (attribute, rawValue, target) => {
            const values = this.normalizeValues(rawValue);
            if (values.length === 0) {
                return;
            }
            if (values.length === 1) {
                target.push(`${attribute}:"${this.escapeFilterValue(values[0])}"`);
                return;
            }
            const orExpression = values
                .map(value => `${attribute}:"${this.escapeFilterValue(value)}"`)
                .join(' OR ');
            target.push(`(${orExpression})`);
        };
    }
}
exports.searchResultTabResultCount = new SearchResultTabResultCount();
